import { useCallback, useEffect, useMemo } from 'react';
import { Platform } from 'react-native';

const THEME_COLOR_META_NAME = 'theme-color';

function getOrCreateThemeColorMeta(): HTMLMetaElement | null {
  if (typeof document === 'undefined') return null;
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${THEME_COLOR_META_NAME}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', THEME_COLOR_META_NAME);
    document.head.appendChild(meta);
  }
  return meta;
}

function setThemeColor(color: string) {
  const meta = getOrCreateThemeColorMeta();
  if (meta) meta.setAttribute('content', color);
}

/**
 * Hook to dynamically control the safe area background color (iOS Safari status bar and home indicator areas).
 * Sets the CSS custom property --safe-area-background and the meta theme-color so the browser chrome matches.
 *
 * @example
 * const { setSafeAreaColor, resetSafeAreaColor } = useSafeAreaColor();
 *
 * // Set a custom color
 * setSafeAreaColor('#FF5500');
 *
 * // Reset to default (based on dark/light mode)
 * resetSafeAreaColor();
 */
export function useSafeAreaColor() {
  const isWeb = Platform.OS === 'web';

  const setSafeAreaColor = useCallback(
    (color: string) => {
      if (!isWeb) return;

      document.documentElement.style.setProperty('--safe-area-background', color);
      setThemeColor(color);
    },
    [isWeb],
  );

  const resetSafeAreaColor = useCallback(() => {
    if (!isWeb) return;

    document.documentElement.style.removeProperty('--safe-area-background');
    // Restore theme-color from CSS variable (e.g. :root or .dark)
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-background')
      .trim();
    if (value) setThemeColor(value);
  }, [isWeb]);

  const getSafeAreaColor = useCallback((): string | null => {
    if (!isWeb) return null;

    return getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-background')
      .trim();
  }, [isWeb]);

  return useMemo(
    () => ({
      setSafeAreaColor,
      resetSafeAreaColor,
      getSafeAreaColor,
    }),
    [setSafeAreaColor, resetSafeAreaColor, getSafeAreaColor],
  );
}

/**
 * Hook that automatically sets the safe area color when the component mounts
 * and resets it when the component unmounts.
 *
 * @example
 * // In a component that needs a specific safe area color
 * useSafeAreaColorEffect('#FF5500');
 */
export function useSafeAreaColorEffect(color: string) {
  const { setSafeAreaColor, resetSafeAreaColor } = useSafeAreaColor();

  useEffect(() => {
    setSafeAreaColor(color);

    return () => {
      resetSafeAreaColor();
    };
  }, [color, setSafeAreaColor, resetSafeAreaColor]);
}
